/**
 * queryClient — the single TanStack Query client for the whole authenticated
 * app, for the app's whole lifetime.
 *
 * Shell: any. Not a component — importable from plain modules (`store.ts`,
 * `sessionBootstrap.ts`) that need to clear it without reaching into the
 * React tree. `main.tsx` wires this exact instance into
 * `QueryClientProvider` and constructs no other; `test-utils.tsx` builds its
 * own throwaway client per render for test isolation, which is unrelated.
 *
 * **Extracted 2026-08-12 (PR #791 fix round, vendor#562/#565).** This object
 * used to be inlined at `main.tsx`'s module scope with no external handle —
 * which is exactly how the cross-member vault leak happened: nothing outside
 * `main.tsx` could clear it, so `lib/store.ts`'s `signOut()` never did.
 *
 * ## Why sign-out clears it
 *
 * Every account-scoped query key carries `currentAccountId`, so switching
 * ACCOUNTS — one user holding seats on several — re-scopes for free and the
 * previous account's entries staying cached under their own keys is
 * correct, not a bug (`lib/store.ts`'s `useSession` docblock). A same-tab
 * USER switch is a different event: two different `auth.users` sharing one
 * browser tab, which is the ordinary shared-booth-device deployment this
 * product is built for (it is why kiosk PIN-exit exists at all). With
 * `staleTime: 30_000` below, a query key that does not vary by user would
 * keep serving the previous user's cached rows for up to 30 seconds after
 * the new user's session replaces theirs. For most of this app that is
 * merely stale — every table is account-wide and any member may see any
 * other member's rows anyway. For the Vault (vendor#562, the first
 * member-private read surface — see `docs/features/vault.md` §4.2) it is a
 * real cross-member disclosure: card titles, cost basis, target price and
 * hold note served to whoever is now signed in, not to whoever they belong
 * to.
 *
 * Query-key-level user-scoping is the primary fix
 * (`features/vault/hooks/useVaultHoldings.ts`'s and
 * `useVaultInstanceExtras.ts`'s keys now carry the caller's user id, so a
 * different user gets a cache miss regardless of this). Clearing here, on
 * every sign-out, is the systemic backstop: the next member-private surface
 * that ships does not have to remember to key by user too.
 *
 * **Also cleared on a same-tab user switch with no sign-out in between**
 * (vendor#799): `sessionBootstrap.ts`'s `hydrateSession` clears this client
 * itself when the incoming `auth.users` id differs from the store's
 * previous one, closing the one event shape the sign-out listener alone
 * does not reach — a `SIGNED_IN` for a different user with no preceding
 * `SIGNED_OUT`. See that function's docblock for the reasoning.
 */
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1, refetchOnWindowFocus: false },
  },
});
